import Icon from "../../../../components/ui/Icon";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import {
  AddIcon,
  AsyncSelect,
  Button,
  FlexContent,
  Form,
  FormField,
  ModalDialog,
  Numeric,
  TableCell,
  TableHeadCell,
  TableRow,
} from "@rothoblaas/design-system";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import Table from "../../../../components/ui/Table";
import { formatCoefficientOrDash } from "../model/coefficientFormat";
import { getDeleteAction, getEditAction } from "../../../../utils/rowActions";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useInitializer from "../../../../hooks/useInitializer";
import useListFilters from "../../../../hooks/useListFilters";
import useI18n from "../../../../hooks/useTranslations";
import { AdminKmodValue, AdminLoadDurationClass, AdminServiceClass } from "../../../../http/lock_calculator";
import { useAdminLockCalculatorApi } from "../../../../http/lock_calculator/useAdminLockCalculator";
import { PaginationQuery } from "../../../../types";

type ServiceClassOption = Pick<AdminServiceClass, "id" | "name">;
type LoadDurationClassOption = Pick<AdminLoadDurationClass, "id" | "name">;

interface FormBody {
  serviceClass: ServiceClassOption | null;
  loadDurationClass: LoadDurationClassOption | null;
  kmod: number | null;
}

const AdminKmodValuesTab: FC = () => {
  const i18n = useI18n();
  const adminApi = useAdminLockCalculatorApi();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ isSearchable: true, prefix: "kmod" });
  const aborter = useRef(new AbortController());
  const debouncedValue = filters.state.q;
  const query: PaginationQuery = { ...filters.state, q: debouncedValue };
  const { data, error } = adminApi.useKmodValues(query, aborter.current.signal);
  const isInitialized = useInitializer(data || error);

  const [itemToDelete, setItemToDelete] = useState<AdminKmodValue>();
  const [itemToEdit, setItemToEdit] = useState<AdminKmodValue>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!data && !error && !isInitialized) return null;

  return (
    <>
      <FlexContent padding>
        <Table<AdminKmodValue>
          filters={filters}
          search={{
            placeholder: i18n.translation.common.search + "...",
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          headingActions={
            <Button onClick={() => setIsCreateOpen(true)}>
              <Icon glyph={AddIcon} />
              {i18n.translation.common.add}
            </Button>
          }
          head={
            <TableRow>
              <TableHeadCell align="left">
                {i18n.translation.lockCalculatorModule.admin.kmodValues.serviceClass}
              </TableHeadCell>
              <TableHeadCell align="left">
                {i18n.translation.lockCalculatorModule.admin.kmodValues.loadDurationClass}
              </TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.kmodValues.kmod}</TableHeadCell>
              <TableHeadCell align="left"></TableHeadCell>
            </TableRow>
          }
          renderRow={(item) => (
            <>
              <TableCell>{item.serviceClass.name}</TableCell>
              <TableCell>{item.loadDurationClass.name}</TableCell>
              <TableCell>{formatCoefficientOrDash(item.kmod)}</TableCell>
            </>
          )}
          onRowClick={(item) => setItemToEdit(item)}
          rowActions={(item) => [
            getEditAction(i18n, () => setItemToEdit(item)),
            getDeleteAction(i18n, () => setItemToDelete(item), true),
          ]}
          items={data?.items}
          total={data?.total}
          error={!!error}
        />

        {!!itemToDelete && (
          <ModalDialog
            onClose={() => setItemToDelete(undefined)}
            title={i18n.translation.lockCalculatorModule.admin.kmodValues.singular}
            isSubmitting={isSubmitting}
            buttons={[
              <Button key={0} onClick={() => setItemToDelete(undefined)} variant="secondary">
                {i18n.translation.common.cancel}
              </Button>,
              <Button
                key={1}
                onClick={() => {
                  setIsSubmitting(true);
                  adminApi
                    .deleteKmodValue(itemToDelete.serviceClass.id, itemToDelete.loadDurationClass.id)
                    .then(() => {
                      dispatch(showSuccessNotification(i18n.translation));
                      setItemToDelete(undefined);
                    })
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                }}
              >
                {i18n.translation.common.yes}
              </Button>,
            ]}
          >
            {i18n.translation.clients.questions.delete}
          </ModalDialog>
        )}
      </FlexContent>

      {isCreateOpen && <FormModal onClose={() => setIsCreateOpen(false)} />}
      {!!itemToEdit && <FormModal item={itemToEdit} onClose={() => setItemToEdit(undefined)} />}
    </>
  );
};

const FormModal: FC<{ item?: AdminKmodValue; onClose: () => void }> = ({ item, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const adminApi = useAdminLockCalculatorApi();
  const [defaultServiceClasses, setDefaultServiceClasses] = useState<ServiceClassOption[]>([]);
  const [defaultLoadDurationClasses, setDefaultLoadDurationClasses] = useState<LoadDurationClassOption[]>([]);
  const scAborter = useRef(new AbortController());
  const ldcAborter = useRef(new AbortController());

  useEffect(() => {
    adminApi
      .getServiceClasses({ limit: 5, q: "" }, scAborter.current.signal)
      .then((items) => {
        setDefaultServiceClasses(items);
        return items;
      })
      .catch(() => setDefaultServiceClasses([]));
    adminApi
      .getLoadDurationClasses({ limit: 5, q: "" }, ldcAborter.current.signal)
      .then((items) => {
        setDefaultLoadDurationClasses(items);
        return items;
      })
      .catch(() => setDefaultLoadDurationClasses([]));
  }, []);

  const loadServiceClasses = async (inputValue: string): Promise<ServiceClassOption[]> => {
    scAborter.current.abort();
    scAborter.current = new AbortController();
    return adminApi.getServiceClasses({ limit: 5, q: inputValue }, scAborter.current.signal);
  };

  const loadLoadDurationClasses = async (inputValue: string): Promise<LoadDurationClassOption[]> => {
    ldcAborter.current.abort();
    ldcAborter.current = new AbortController();
    return adminApi.getLoadDurationClasses({ limit: 5, q: inputValue }, ldcAborter.current.signal);
  };

  const defaultValues: FormBody = {
    serviceClass: item?.serviceClass ?? null,
    loadDurationClass: item?.loadDurationClass ?? null,
    kmod: item?.kmod ?? null,
  };

  const optionLabel = (x: { name: string }) => x.name;

  const onSubmit = () => async (formData: FormBody) => {
    if (!formData.serviceClass || !formData.loadDurationClass || formData.kmod == null) return;
    try {
      if (item) {
        await adminApi.updateKmodValue(item.serviceClass.id, item.loadDurationClass.id, { kmod: formData.kmod });
      } else {
        const body = {
          serviceClassId: formData.serviceClass.id,
          loadDurationClassId: formData.loadDurationClass.id,
          kmod: formData.kmod,
        };
        await adminApi.insertKmodValue(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        item
          ? `${i18n.translation.common.edit} ${i18n.translation.lockCalculatorModule.admin.kmodValues.singular}`
          : `${i18n.translation.common.add} ${i18n.translation.lockCalculatorModule.admin.kmodValues.singular}`
      }
    >
      <Form<FormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}

        defaultValues={defaultValues}
        secondaryButton={
          <Button onClick={onClose} variant="secondary">
            {i18n.translation.common.cancel}
          </Button>
        }
      >
        {({ control }) => (
          <FormFieldsContainer fullWidth>
            <Controller
              name="serviceClass"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.kmodValues.serviceClass}
                  error={fieldState.error}
                >
                  {({ labelId }) => (
                    <AsyncSelect<ServiceClassOption, false>
                      {...field}
                      inputId={labelId}
                      defaultOptions={defaultServiceClasses}
                      loadOptions={loadServiceClasses}
                      isDisabled={!!item}
                      placeholder={i18n.translation.lockCalculatorModule.selectPlaceholder}
                      getOptionLabel={optionLabel}
                      getOptionValue={(x) => x.id}
                      isInvalid={fieldState.invalid}
                      value={defaultServiceClasses.find((opt) => opt.id === field.value?.id) ?? field.value}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="loadDurationClass"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.kmodValues.loadDurationClass}
                  error={fieldState.error}
                >
                  {({ labelId }) => (
                    <AsyncSelect<LoadDurationClassOption, false>
                      {...field}
                      inputId={labelId}
                      defaultOptions={defaultLoadDurationClasses}
                      loadOptions={loadLoadDurationClasses}
                      isDisabled={!!item}
                      placeholder={i18n.translation.lockCalculatorModule.selectPlaceholder}
                      getOptionLabel={optionLabel}
                      getOptionValue={(x) => x.id}
                      isInvalid={fieldState.invalid}
                      value={defaultLoadDurationClasses.find((opt) => opt.id === field.value?.id) ?? field.value}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="kmod"
              control={control}
              rules={{ required: true, min: 0, max: 2 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.lockCalculatorModule.admin.kmodValues.kmod} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={3}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default AdminKmodValuesTab;
